import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { Navbar } from '../components/Navbar';
import { getRoles, createRole, updateRole, deleteRole, type Role, type RoleServiceError } from '../services/role.service';
import { ALL_PERMISSIONS, type Permission } from '../services/appUser.service';
import { Badge, BackButton, Button, Icon, PencilIcon, Spinner, TrashIcon, PermissionGate } from '../components/core';
import toast from 'react-hot-toast';

export default function RolesPage() {
  const { t } = useTranslation();
  const { org, orgId, orgSlug } = useOrg();
  const { user, signOut } = useAuth();
  usePermissions();
  const navigate = useNavigate();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Load roles on mount
  useEffect(() => {
    if (!orgId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const rolesData = await getRoles(orgId);
        setRoles(rolesData.sort((a, b) => {
          // System roles first, then by name
          if (a.isSystem && !b.isSystem) return -1;
          if (!a.isSystem && b.isSystem) return 1;
          return a.name.localeCompare(b.name);
        }));
      } catch (err) {
        const serviceErr = err as RoleServiceError;
        setError(serviceErr.message || t('roles.loadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orgId, t]);

  const handleCreateClick = () => {
    setFormName('');
    setSelectedPermissions([]);
    setEditingRole(null);
    setShowCreateForm(true);
  };

  const handleEditClick = (role: Role) => {
    setFormName(role.name);
    setSelectedPermissions([...role.permissions]);
    setEditingRole(role);
    setShowCreateForm(true);
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
    setEditingRole(null);
    setFormName('');
    setSelectedPermissions([]);
  };

  const handleFormSubmit = async () => {
    if (!orgId || !formName.trim()) {
      toast.error(t('roles.roleNameRequired'));
      return;
    }

    setIsSaving(true);
    try {
      if (editingRole) {
        await updateRole(orgId, editingRole.id, formName, selectedPermissions);
        toast.success(t('roles.toastSaved'));
        // Update local state
        setRoles(roles.map(r =>
          r.id === editingRole.id
            ? { ...r, name: formName, permissions: selectedPermissions }
            : r
        ));
      } else {
        await createRole(orgId, formName, selectedPermissions);
        toast.success(t('roles.toastCreated'));
        // Reload roles
        const updated = await getRoles(orgId);
        setRoles(updated);
      }
      handleFormCancel();
    } catch (err) {
      const serviceErr = err as RoleServiceError;
      toast.error(serviceErr.message || t('roles.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = async (role: Role) => {
    if (!orgId || !window.confirm(`Delete role "${role.name}"?`)) return;

    setIsSaving(true);
    try {
      await deleteRole(orgId, role.id);
      toast.success(t('roles.toastDeleted'));
      setRoles(roles.filter(r => r.id !== role.id));
    } catch (err) {
      const serviceErr = err as RoleServiceError;
      toast.error(serviceErr.message || t('roles.deleteError'));
    } finally {
      setIsSaving(false);
    }
  };

  const togglePermission = (perm: Permission) => {
    setSelectedPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <BackButton />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-gray-900">
              {t('roles.title')} ({roles.length})
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('roles.subtitle')}
            </p>
          </div>
          <PermissionGate permission="users.manage">
            <Button onClick={handleCreateClick} disabled={showCreateForm || isSaving}>
              + {t('roles.newRole')}
            </Button>
          </PermissionGate>
        </div>

        {error && (
          <div className="mb-4 rounded bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 font-display text-lg font-semibold">
              {editingRole ? `${t('roles.editTitle')}: ${editingRole.name}` : t('roles.newRoleTitle')}
            </h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('roles.roleNameLabel')}
              </label>
              <input
                type="text"
                value={formName}
                onChange={e => setFormName(e.target.value)}
                disabled={isSaving}
                className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 font-body text-sm focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:bg-gray-50"
                placeholder={t('roles.roleNamePlaceholder')}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('roles.permissionsTitle')}
              </label>
              <div className="space-y-2">
                {ALL_PERMISSIONS.map(perm => (
                  <label key={perm} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPermissions.includes(perm)}
                      onChange={() => togglePermission(perm)}
                      disabled={isSaving}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:opacity-50"
                    />
                    <span className="ms-2 font-body text-sm text-gray-700">
                      {perm}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleFormCancel}
                disabled={isSaving}
              >
                {t('roles.cancel')}
              </Button>
              <Button onClick={handleFormSubmit} disabled={isSaving}>
                {isSaving ? t('roles.saving') : editingRole ? t('roles.save') : t('roles.create')}
              </Button>
            </div>
          </div>
        )}

        {/* Roles List */}
        <div className="space-y-3">
          {roles.length === 0 ? (
            <div className="rounded bg-gray-50 px-4 py-6 text-center">
              <p className="font-body text-sm text-gray-500">
                {t('roles.empty')}
              </p>
            </div>
          ) : (
            roles.map(role => (
              <div
                key={role.id}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-base font-semibold text-gray-900">
                        {role.name}
                      </h3>
                      {role.isSystem && (
                        <Badge variant="secondary">{t('roles.systemBadge')}</Badge>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">
                      {role.permissions.length === 0
                        ? t('roles.emptyHelp')
                        : `${role.permissions.length} permission${role.permissions.length !== 1 ? 's' : ''}`}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {role.permissions.map(perm => (
                        <span
                          key={perm}
                          className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
                        >
                          {perm}
                        </span>
                      ))}
                    </div>
                  </div>

                  {!role.isSystem && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(role)}
                        disabled={isSaving}
                        className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
                        aria-label={t('roles.editLabel')}
                      >
                        <Icon size="sm">
                          <PencilIcon />
                        </Icon>
                      </button>
                      <button
                        onClick={() => handleDeleteClick(role)}
                        disabled={isSaving}
                        className="p-2 text-red-600 hover:text-red-900 disabled:opacity-50"
                        aria-label={t('roles.deleteLabel')}
                      >
                        <Icon size="sm">
                          <TrashIcon />
                        </Icon>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
